"use strict";

let feedSlots = [];

/**
 * Creates and returns a script element to trigger Ezoic ad display for a given ad ID.
 *
 * Generates a `<script>` element that, when appended to the DOM, attempts to push
 * a command to the Ezoic `ezstandalone` queue to show ads for the provided ad ID. If the 
 * `ezstandalone` object is not found (possibly due to Ezoic being disabled), a warning is 
 * logged in the console to indicate the missing dependency.
 */
window.getEzoicScriptElement = adId => {
  const ezoicScript = document.createElement('script');
  ezoicScript.innerHTML = `
        try {
            ezstandalone.cmd.push(() => {
                ezstandalone.showAds(${adId});
            });
        } catch (e) {
            console.warn("[DFP] The 'ezstandalone' not found, is EZOIC turned off in 'ezo_enabled' setting?", e);
        }
    `;
  return ezoicScript;
};
class GoogleAds {
  targetScreenSizes = {
    '1024': [1024, 768],
    '1280': [1280, 1024],
    '1366': [1366, 768],
    '1370': [1370, 768],
    '1440': [1440, 900],
    '1600': [1600, 1050],
    '1920': [1920, 1080]
  };
  dfpViewApiUrl = `${window.baseApiUrl}/dfp/view`;
  dfpSlotSelector = '[dfp-slot]';
  defaultSlotAttrSelector = 'dfp-slot';
  slotPremiumClassName = 'dfp_premium';
  slotDesktopCheckup = '24_';
  slotMobileCheckup = '24m_';
  slotSubscribeCheckup = 'premium';
  feedPlaceholderCounter = 800;
  articleFeedPlaceholderCounter = 810;
  googlePreviewParam = new URLSearchParams(window.location.search).get('google_preview');
  constructor(options) {
    this.googleTag = options.googleTag;
    this.dfpConfig = options.dfpConfig;
    this.isPremiumUser = this.dfpConfig.isPremium;
    this.slotsData = [];
    this.placeholderNumber = null;
  }

  /**
   * Return data of client screen size pixels by width and height.
   * First available value i assigned to appropriate key
   */
  static getScreenSize() {
    return {
      width: window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
      height: window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
    };
  }
  static getPlatform() {
    return window.getPlatform();
  }
  static isMobile() {
    return this.getPlatform() === 'Mobile';
  }
  async fetchSlotsData() {
    let dfpSlugs = [this.dfpConfig.slug, `${this.dfpConfig.slug}_mobile`];
    if (this.isPremiumUser) {
      dfpSlugs = [`${this.dfpConfig.slug}_${this.slotSubscribeCheckup}`, `${this.dfpConfig.slug}_mobile_${this.slotSubscribeCheckup}`];
    }
    await Promise.all(dfpSlugs.map(async slug => {
      const dfpResponse = await fetch(`${this.dfpViewApiUrl}/${slug}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/vnd.api+json'
        }
      });
      if (dfpResponse.status === 200) {
        const dfpData = await dfpResponse.json();
        this.slotsData.push(...dfpData.data.attributes.slots);
      } else {
        console.warn(`[DFP] Initialization of Google Ads Services skipped for '${slug}'. ` + `DFP API for slug '${slug}' returned status '${dfpResponse.status}'.`);
      }
    }));
    return this.slotsData;
  }
  getSlots(newFeedDfp) {
    let filteredSlots = [];
    const feedSlotsData = [];
    if (newFeedDfp && feedSlots.length) {
      feedSlots.forEach(elem => {
        Array.from(elem.attributes).forEach(el => {
          if (el.name.startsWith('dfp-slot') && el.value) {
            feedSlotsData.push(...this.slotsData.filter(slot => slot.div.includes(el.value)));
          }
        });
      });
    }
    const dfpSlotsData = newFeedDfp ? feedSlotsData : this.slotsData;
    if (dfpSlotsData.length > 0) {
      if (this.constructor.isMobile()) {
        filteredSlots = dfpSlotsData.filter(slot => slot.slot.includes(this.slotMobileCheckup));
      } else {
        filteredSlots = dfpSlotsData.filter(slot => slot.slot.includes(this.slotDesktopCheckup));
      }
      if (this.isPremiumUser) {
        filteredSlots = filteredSlots.filter(slot => slot.slot.includes(this.slotSubscribeCheckup));
      } else {
        filteredSlots = filteredSlots.filter(slot => !slot.slot.includes(this.slotSubscribeCheckup));
      }
    }
    if (!newFeedDfp) {
      filteredSlots.forEach(slot => {
        slot.size = JSON.parse(slot.size.replace("'fluid'", '"fluid"'));
      });
    }
    feedSlots = [];
    return filteredSlots;
  }

  /**
   * Constructs target values for resolutions, ie:
   * `['1024x768_min', '1280x1024_min', '1366x768_min', '1370x768_min']`
   */
  getTargetScreenSizes() {
    const currentScreenSize = this.constructor.getScreenSize();
    const targetSizes = [];
    Object.keys(this.targetScreenSizes).forEach(screenSize => {
      if (currentScreenSize.width >= Number(screenSize)) {
        targetSizes.push(`${screenSize}x${this.targetScreenSizes[screenSize][1]}_min`);
      }
    });
    return targetSizes;
  }
  collapseSlotBeforeAdFetch(slot) {
    if (this.isPremiumUser) return this.isPremiumUser;
    if (slot && slot.collapse !== undefined) return slot.collapse;
    return true;
  }
  displayBanner(dfpSlotData) {
    this.googleTag.display(dfpSlotData.getSlotElementId());
    // eslint-disable-next-line no-undef
    if (this.googleTag.pubads().isInitialLoadDisabled() && !isAdPushUpScriptEnabled) {
      this.googleTag.pubads().refresh([dfpSlotData]);
    }
  }
  initDfpSlot(slot) {
    let slotName = slot.div.split('div')[1];
    if (this.isPremiumUser) slotName += `_${this.slotSubscribeCheckup}`;
    let slotDivCollection = null;
    let attrSelector = this.defaultSlotAttrSelector;
    if (this.constructor.isMobile()) attrSelector += '-mobile';
    if (this.isPremiumUser) attrSelector += `-${this.slotSubscribeCheckup}`;
    slotDivCollection = Array.from(document.querySelectorAll(`[${attrSelector}='${slotName}']`)).filter(e => e.dataset.slotUsed !== 'true');
    if (slotDivCollection.length) {
      const dfpSlot = [];
      slotDivCollection.forEach(slotDiv => {
        if (this.isPremiumUser) slotDiv.classList.add(this.slotPremiumClassName);
        const randomVal = ((1 + Math.random()) * 0x10000 || 0).toString(16).substring(1);
        const slotId = `dfp_slot_${randomVal}`;
        slotDiv.id = slotId;
        let dfpSlotData = null;
        if (slot.is_out_of_page) {
          let divValue = slotId;
          if (slot.slot.toLowerCase().includes('interstitial')) {
            divValue = this.googleTag.enums.OutOfPageFormat.INTERSTITIAL;
          }
          dfpSlotData = this.googleTag.defineOutOfPageSlot(slot.slot, divValue);
        } else {
          if (!this.googleTag || typeof this.googleTag.defineSlot !== 'function') {
            console.warn('this.googleTag is not defined or does not have defineSlot');
            return;
          }
          dfpSlotData = this.googleTag.defineSlot(slot.slot, slot.size, slotId);
          dfpSlotData.defineSizeMapping([]);
          let {
            extraTarget
          } = slotDiv.dataset;
          try {
            extraTarget = extraTarget !== undefined ? JSON.parse(extraTarget) : extraTarget;
          } catch (e) {
            console.warn(`[DFP] 'extraTarget' for '${slotName}' is not valid JSON. \n ${e}`);
          }
          if (extraTarget) {
            Object.entries(extraTarget).forEach(([key, value]) => {
              dfpSlotData.setTargeting(key, value);
            });
          }
        }
        dfpSlotData.setCollapseEmptyDiv(true, this.collapseSlotBeforeAdFetch(slot));
        dfpSlotData.addService(this.googleTag.pubads());
        dfpSlotData.setTargeting(slot.targeting_key, slot.targeting_value);
        dfpSlotData.isDynamic = slot.is_dynamic;
        dfpSlotData.isOutOfPage = slot.is_out_of_page;
        dfpSlotData.slotElement = slotDiv;
        slotDiv.dataset.slotUsed = true;
        if (slotDiv.dataset.isLazy) {
          if (window.isInViewport(slotDiv)) {
            this.displayBanner(dfpSlotData);
          } else {
            /**
             * 'AbortController' allows the function to execute
             * until abort is called and then terminates it.
             * It has the same effect as 'removeEventListener'
             * which didn't work here.
             */
            const controller = new AbortController();
            window.addEventListener('scroll', window.debounce(() => {
              if (window.isInViewport(slotDiv)) {
                this.displayBanner(dfpSlotData);
                controller.abort();
              }
            }, 10), {
              passive: true,
              signal: controller.signal
            });
            const lazyLoadController = new AbortController();
            document.addEventListener('lazyLoadDisplay', event => {
              const elementId = event.detail.querySelector('.js_dfpBanner')?.id;
              if (elementId === slotDiv.id) {
                this.displayBanner(dfpSlotData);
                lazyLoadController.abort();
              }
            }, {
              signal: lazyLoadController.signal
            });
          }
        } else dfpSlot.push(dfpSlotData);
        this.setupEzoic(slotDiv);
      });
      return dfpSlot;
    }
    console.warn(`[DFP] There is no defined slot placement for '${slotName}' but position '${slot.div}' is defined on view.`);
  }
  getAllSlotElemets() {
    return document.querySelectorAll(this.dfpSlotSelector);
  }
  closeUnusedSlots() {
    const unusedSlotElements = Array.from(this.getAllSlotElemets()).filter(e => e.dataset.slotUsed !== 'true');
    unusedSlotElements?.forEach(element => {
      element.style.display = 'none';
      const {
        previousElementSibling
      } = element;
      if (previousElementSibling && previousElementSibling.classList.contains('js_slotRemove')) previousElementSibling.remove();
    });
  }
  setupEzoic(slotDiv) {
    if (!slotDiv || !slotDiv.hasAttribute('data-ezoic-placeholder-mob') && !slotDiv.hasAttribute('data-ezoic-placeholder-desk')) {
      return;
    }

    // Determine the placeholder number based on the platform (mobile or desktop)
    let placeholderNumber = window.isMob ? slotDiv.dataset.ezoicPlaceholderMob : slotDiv.dataset.ezoicPlaceholderDesk;

    // Exit conditions for specific placeholder logic
    const isMobileSlot = slotDiv.getAttribute('dfp-slot-mobile');
    if (document.querySelector('.section--article') && (placeholderNumber === '800' && isMobileSlot === '24m_inArticle' && this.feedPlaceholderCounter > 810 || placeholderNumber === '810' && isMobileSlot === '24m_rectangle_feed_bot' && this.articleFeedPlaceholderCounter > 825) || placeholderNumber === '800' && this.feedPlaceholderCounter > 850) return;

    // Update placeholder number for specific cases
    if (placeholderNumber === '810' && isMobileSlot === '24m_rectangle_feed_bot') {
      placeholderNumber = this.articleFeedPlaceholderCounter++;
    } else if (placeholderNumber === '800') {
      placeholderNumber = this.feedPlaceholderCounter++;
    }

    // Create the ad div and insert it into the DOM
    const adDiv = document.createElement('div');
    adDiv.id = `ezoic-pub-ad-placeholder-${placeholderNumber}`;
    slotDiv.parentNode.insertBefore(adDiv, slotDiv);

    // Create and insert the ad script
    const adScript = window.getEzoicScriptElement(placeholderNumber);
    adDiv.parentNode.insertBefore(adScript, adDiv.nextSibling);
  }

  /**
   * If the AdPushup script is enabled and doesn't exist yet,
   * its code is placed at the top of the page.
   */
  insertAdpushupScript() {
    this.adpushupOnPage = document.getElementById('adpushup');

    // eslint-disable-next-line no-undef
    if (isAdPushUpScriptEnabled && !this.adpushupOnPage) {
      const adpushupScript = document.createElement('script');
      adpushupScript.src = '//cdn.adpushup.com/42773/adpushup.js';
      adpushupScript.crossOrigin = 'anonymous';
      adpushupScript.type = 'text/javascript';
      adpushupScript.async = true;
      adpushupScript.id = 'adpushup';
      (document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]).appendChild(adpushupScript);
    }
  }
  activateDfpSlots(slotsConfig, newFeedDfp) {
    this.slotsConfig = slotsConfig;
    this.currentPlatform = this.constructor.getPlatform();
    const {
      userId
    } = this.dfpConfig;
    const targetScreenSizes = this.getTargetScreenSizes();
    if (!newFeedDfp) {
      /**
       * The order of function calls within 'googletag' is important:
       * https://developers.google.com/publisher-tag/common_implementation_mistakes#scenario-6:-mis-ordering-calls-to-gpt
       */
      this.googleTag.pubads().setTargeting('site', this.dfpConfig.site);
      this.googleTag.pubads().setTargeting('platform', this.currentPlatform.toLocaleLowerCase());
      this.googleTag.pubads().setTargeting('domain', window.location.host);
      this.googleTag.pubads().setTargeting('userType', userId ? 'logged user' : 'non logged user');
      if (targetScreenSizes.length) {
        this.googleTag.pubads().setTargeting('res_min', targetScreenSizes);
      }
      Object.keys(this.dfpConfig.pageTargeting).forEach(targetName => {
        this.googleTag.pubads().setTargeting(targetName, this.dfpConfig.pageTargeting[targetName]);
      });
      this.googleTag.pubads().collapseEmptyDivs();
      // eslint-disable-next-line no-undef
      if (isAdPushUpHBEnabled) this.googleTag.pubads().disableInitialLoad();
      if (this.slotsConfig.enableUserPPID && userId) {
        this.googleTag.pubads().setPublisherProvidedId(window.getUserPPID(userId));
      }
    }
    const slots = this.getSlots(newFeedDfp).map(slot => this.initDfpSlot(slot)).flat().filter(e => e !== undefined);
    if (!newFeedDfp) {
      this.googleTag.enableServices();
      this.insertAdpushupScript();
    }
    const isYLenabled = this.constructor.isMobile() ? this.slotsConfig.isYLenabledMobile : this.slotsConfig.isYLenabledDesktop;
    if (slots.length) {
      slots.forEach(slot => {
        if (window.YLHH && isYLenabled && slot.isDynamic && !slot.isOutOfPage) {
          window.YLHH.bidder.startAuction([slot.getSlotId().getAdUnitPath()]);
        } else {
          this.googleTag.display(slot.getSlotElementId());
          // eslint-disable-next-line no-undef
          if (this.googleTag.pubads().isInitialLoadDisabled() && !isAdPushUpScriptEnabled) {
            this.googleTag.pubads().refresh([slot]);
          }
        }
      });
    }
    this.closeUnusedSlots();
  }
}
const googleAds = new GoogleAds({
  googleTag: window.googletag,
  dfpConfig: window.dfpConfig
});
document.addEventListener('BaseDataLoaded', async () => {
  const slotsConfig = {
    enableUserPPID: Boolean(window.getSettingsFromApi('ppid_set_enabled')),
    isYLenabledDesktop: window.getSettingsFromApi('yieldlove_desktop'),
    isYLenabledMobile: window.getSettingsFromApi('yieldlove_mobile')
  };
  await googleAds.fetchSlotsData();
  function attemptSlotInit() {
    if (window.googletag && window.googletag.apiReady) {
      window.googletag.cmd.push(googleAds.activateDfpSlots(slotsConfig));
    } else {
      setTimeout(() => {
        attemptSlotInit();
      }, 300);
    }
  }
  attemptSlotInit();
  window.initProcessingSubsequentlyAddedDfpSlots = (selector = '#feedPagesPrevious', specificSelector = false) => {
    const dfpSelector = specificSelector ? `${specificSelector} [dfp-slot]` : '[dfp-slot]';
    const newDfpSlots = document.querySelector(selector)?.querySelectorAll(dfpSelector);
    feedSlots = Array.from(newDfpSlots).filter(e => e.dataset.slotUsed !== 'true');
    if (feedSlots.length) googleAds.activateDfpSlots(slotsConfig, true);
  };
});

// Close possibly opened slots if premium article content is loaded
document.addEventListener('PremiumArticleContentLoaded', () => {
  googleAds.closeUnusedSlots();
});